import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Loader2 } from 'lucide-react';
import { API_URL } from '../utils/formatters';

const zar = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

/**
 * Selected investor's position on a chosen date (user 2026-10-13): loan
 * holdings, approved general-ledger cash, Deal Room holding and the total.
 * Backend: GET /admin/investor-transactions/reconciliation/investor-position.
 * `refreshKey` changes after every reconciliation refresh so the cash figure
 * follows matches / verifies as you work.
 */
export function InvestorPositionPanel({ headers, investorId, refreshKey }) {
  const [asOf, setAsOf] = useState(today());
  const [pos, setPos] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!investorId || !asOf) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API_URL}/api/admin/investor-transactions/reconciliation/investor-position`,
      { headers, params: { investor_id: investorId, as_of: asOf } })
      .then(r => { if (!cancelled) setPos(r.data); })
      .catch(e => { if (!cancelled) setError(e.response?.data?.detail || 'Could not load the position'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [investorId, asOf, refreshKey]);

  if (!investorId) return null;
  const tile = (label, value, sub, testid, strong) => (
    <div className={`rounded-lg border p-3 ${strong ? 'bg-slate-900 text-white border-slate-900' : 'bg-white'}`} data-testid={testid}>
      <p className={`text-xs ${strong ? 'text-white/70' : 'text-gray-500'}`}>{label}</p>
      <p className="text-lg font-bold font-mono">{zar(value)}</p>
      {sub && <p className={`text-[11px] ${strong ? 'text-white/60' : 'text-gray-400'}`}>{sub}</p>}
    </div>
  );

  return (
    <Card data-testid="recon-investor-position">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium">Position as at</span>
          <Input type="date" value={asOf} onChange={e => setAsOf(e.target.value)} className="w-40 h-8" data-testid="recon-position-date" />
          <button type="button" className="text-xs text-blue-700 underline" onClick={() => setAsOf(today())}>today</button>
          {loading && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          {error && <span className="text-xs text-red-700">{error}</span>}
        </div>
        {pos && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {tile('Loan holdings', pos.loans, `${pos.loans_count} loan(s) · ${pos.loans_source || '—'}`, 'recon-position-loans')}
            {tile('Cash (approved GL)', pos.cash, 'reconciled transactions only', 'recon-position-cash')}
            {tile('Deal Room', pos.deal_room, 'at cost, reconciled investments', 'recon-position-dealroom')}
            {tile('Portfolio value', pos.portfolio, `as at ${pos.as_of}`, 'recon-position-total', true)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default InvestorPositionPanel;
